import { t } from 'i18next';

/** Duration-based condition operators (values are stored in hours). */
export type MarketingDurationOperator =
    | 'BEFORE_HOURS'
    | 'WITHIN_LAST_HOURS'
    | 'AFTER_HOURS'
    | 'WITHIN_NEXT_HOURS';

/** Short operator keys shown in the duration direction dropdown. */
export type DurationOperatorUi = 'WITHIN' | 'BEFORE' | 'AFTER' | 'NEXT';

const LEGACY_TO_HOURS_OPERATOR: Record<string, MarketingDurationOperator> = {
    BEFORE_DAYS: 'BEFORE_HOURS',
    WITHIN_LAST_DAYS: 'WITHIN_LAST_HOURS',
    AFTER_DAYS: 'AFTER_HOURS',
    WITHIN_NEXT_DAYS: 'WITHIN_NEXT_HOURS',
};

const UI_TO_HOURS_OPERATOR: Record<DurationOperatorUi, MarketingDurationOperator> = {
    WITHIN: 'WITHIN_LAST_HOURS',
    BEFORE: 'BEFORE_HOURS',
    AFTER: 'AFTER_HOURS',
    NEXT: 'WITHIN_NEXT_HOURS',
};

const HOURS_TO_UI_OPERATOR: Record<MarketingDurationOperator, DurationOperatorUi> = {
    WITHIN_LAST_HOURS: 'WITHIN',
    BEFORE_HOURS: 'BEFORE',
    AFTER_HOURS: 'AFTER',
    WITHIN_NEXT_HOURS: 'NEXT',
};

export const DURATION_OPERATOR_UI_OPTIONS: Array<{ label: string; value: DurationOperatorUi }> = [
    { label: t('Marketing.DurationOperatorWithin'), value: 'WITHIN' },
    { label: t('Marketing.DurationOperatorBefore'), value: 'BEFORE' },
    { label: t('Marketing.DurationOperatorAfter'), value: 'AFTER' },
    { label: t('Marketing.DurationOperatorNext'), value: 'NEXT' },
];

export const DURATION_OPERATORS_WITHIN_AFTER: DurationOperatorUi[] = ['WITHIN', 'AFTER'];

export const DURATION_OPERATORS_WITHIN_BEFORE_AFTER: DurationOperatorUi[] = ['WITHIN', 'BEFORE', 'AFTER'];

export const DURATION_OPERATORS_WITHIN_NEXT: DurationOperatorUi[] = ['WITHIN', 'NEXT'];

export function getDurationOperatorOptions(
    allowed: DurationOperatorUi[] = ['WITHIN', 'BEFORE', 'AFTER', 'NEXT'],
): Array<{ label: string; value: DurationOperatorUi }> {
    return DURATION_OPERATOR_UI_OPTIONS.filter((option) => allowed.includes(option.value));
}

/** Normalize legacy *_DAYS operators to *_HOURS for UI and new payloads. */
export function normalizeDurationOperator(operator?: string | null): string {
    if (!operator) return 'BEFORE_HOURS';
    return LEGACY_TO_HOURS_OPERATOR[operator as keyof typeof LEGACY_TO_HOURS_OPERATOR] ?? operator;
}

export function durationOperatorFromUi(ui?: DurationOperatorUi | string | null): MarketingDurationOperator {
    if (ui && ui in UI_TO_HOURS_OPERATOR) {
        return UI_TO_HOURS_OPERATOR[ui as DurationOperatorUi];
    }
    return 'BEFORE_HOURS';
}

export function durationOperatorToUi(operator?: string | null): DurationOperatorUi {
    const normalized = normalizeDurationOperator(operator) as MarketingDurationOperator;
    return HOURS_TO_UI_OPERATOR[normalized] ?? 'BEFORE';
}

export function isMarketingDurationOperator(operator?: string | null): boolean {
    const normalized = normalizeDurationOperator(operator);
    return (
        normalized === 'BEFORE_HOURS' ||
        normalized === 'WITHIN_LAST_HOURS' ||
        normalized === 'AFTER_HOURS' ||
        normalized === 'WITHIN_NEXT_HOURS'
    );
}

export function isBeforeDurationOperator(operator?: string | null): boolean {
    return operator === 'BEFORE_HOURS' || operator === 'BEFORE_DAYS';
}

export function isWithinLastDurationOperator(operator?: string | null): boolean {
    return operator === 'WITHIN_LAST_HOURS' || operator === 'WITHIN_LAST_DAYS';
}

export function isAfterDurationOperator(operator?: string | null): boolean {
    return operator === 'AFTER_HOURS' || operator === 'AFTER_DAYS';
}

export function isWithinNextDurationOperator(operator?: string | null): boolean {
    return operator === 'WITHIN_NEXT_HOURS' || operator === 'WITHIN_NEXT_DAYS';
}

/** Booking datetime sub-conditions: BEFORE uses conditionId 6; other duration operators use 30. */
export function resolveBookingDaysConditionId(operator?: string | null): number {
    return isBeforeDurationOperator(normalizeDurationOperator(operator)) ? 6 : 30;
}
